import { DOCUMENT_TYPE_KEYS, } from './document';
/**
 * The three document templates (design 2026-10-31, C1). Configuration in code,
 * not database content. The real OSCOM texts exist only in the confidential
 * reference document and never enter the repository: every text block is a
 * visible placeholder marked `placeholder`, so the hints can find it.
 */
/** Visible stand-in for an OSCOM text block. */
export function placeholderText(name) {
    return `[Textbaustein ${name} – bitte ergänzen]`;
}
const GENERAL = {
    key: 'general',
    title: 'Allgemeines',
    required: true,
    children: [
        { key: 'terms', title: 'Begriffe', required: true, boilerplate: [placeholderText('Begriffe')] },
        {
            key: 'rating-legend',
            title: 'Symbollegende Ampel',
            required: true,
            boilerplate: [placeholderText('Symbollegende Ampel')],
        },
        {
            key: 'document-history',
            title: 'Dokumentenhistorie',
            required: true,
            boilerplate: [placeholderText('Dokumentenhistorie')],
        },
    ],
};
const ALERTING = {
    key: 'alerting',
    title: 'Alarmierung & Kommunikation',
    required: true,
    boilerplate: [placeholderText('Alarmierung & Kommunikation')],
};
/** Empty on purpose: site image and text are added by hand. */
const SITE_OVERVIEW = {
    key: 'site-overview',
    title: 'Übersicht Gelände',
    required: true,
};
const BEACON_POSITIONS = {
    key: 'beacon-positions',
    title: 'Beacon-Positionen',
    required: true,
    generator: { kind: 'beaconPositions', blocksPlacement: 'before' },
};
const MEASUREMENT_POINTS = {
    key: 'measurement-points',
    title: 'Funkmesspunkte',
    required: true,
    generator: { kind: 'measurementPoints', blocksPlacement: 'before' },
};
const SUMMARY = {
    key: 'summary',
    title: 'Zusammenfassung',
    required: true,
    generator: { kind: 'summary', blocksPlacement: 'after' },
};
const DISCLAIMER = {
    key: 'disclaimer',
    title: 'Haftungsausschluss',
    required: true,
    boilerplate: [placeholderText('Haftungsausschluss')],
};
export const DOCUMENT_TEMPLATES = [
    {
        key: 'beacon-gsm-concept',
        name: 'Gesamtvermessung (Beacon und Mobilfunk)',
        sections: [
            GENERAL,
            ALERTING,
            SITE_OVERVIEW,
            BEACON_POSITIONS,
            MEASUREMENT_POINTS,
            SUMMARY,
            DISCLAIMER,
        ],
    },
    {
        key: 'beacon-survey',
        name: 'Beaconvermessung',
        sections: [GENERAL, ALERTING, SITE_OVERVIEW, BEACON_POSITIONS, SUMMARY, DISCLAIMER],
    },
    {
        key: 'mobile-survey',
        name: 'Mobilfunkvermessung',
        sections: [GENERAL, SITE_OVERVIEW, MEASUREMENT_POINTS, SUMMARY, DISCLAIMER],
    },
];
export function isDocumentTypeKey(value) {
    return typeof value === 'string' && DOCUMENT_TYPE_KEYS.includes(value);
}
export function documentTemplate(key) {
    return DOCUMENT_TEMPLATES.find((template) => template.key === key);
}
/** Every chapter of the template, depth first. */
export function templateNodes(template) {
    const walk = (nodes) => nodes.flatMap((node) => [node, ...walk(node.children ?? [])]);
    return walk(template.sections);
}
export function templateHasGenerator(template, kind) {
    return templateNodes(template).some((node) => node.generator?.kind === kind);
}
/** Templates with the measurement point chapter need at least one series. */
export function requiresSeries(template) {
    return templateHasGenerator(template, 'measurementPoints');
}
export function isRequiredTemplateKey(template, key) {
    if (!template || !key)
        return false;
    return templateNodes(template).some((node) => node.key === key && node.required);
}
/**
 * Series whose location lies at or below `siteLocationId`. A series without a
 * location, or with a location that no longer exists, is never in scope.
 */
export function seriesBelowLocation(series, locationsById, siteLocationId) {
    return series.filter((entry) => {
        const location = entry.siteLocationId ? locationsById.get(entry.siteLocationId) : undefined;
        return location?.path.includes(siteLocationId) ?? false;
    });
}
//# sourceMappingURL=document-types.js.map